import { useCallback, useEffect, useRef } from 'react';

type OverlayBackOptions = {
  enabled?: boolean;
};

type OverlayEntry = {
  historyKey: string;
  close: () => void;
};

const overlayStack: OverlayEntry[] = [];

/** Cierra el overlay superior (llamado desde App.tsx al detectar popstate). */
export function handleOverlayBack(): boolean {
  if (overlayStack.length === 0) return false;
  const top = overlayStack[overlayStack.length - 1];
  overlayStack.pop();
  top.close();
  return true;
}

export function getOverlayStackDepth(): number {
  return overlayStack.length;
}

/**
 * Sincroniza un overlay/modal con el botón Back del teléfono.
 * El popstate global lo maneja App.tsx vía handleOverlayBack().
 * Usar el `requestClose` devuelto en botones X y acciones de cancelar.
 */
export function useOverlayBackButton(
  open: boolean,
  onClose: () => void,
  historyKey: string,
  options?: OverlayBackOptions,
): () => void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const entryRef = useRef<OverlayEntry | null>(null);
  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!open || !enabled) return undefined;

    window.history.pushState({ page: historyKey, overlay: true }, '', '');

    const entry: OverlayEntry = {
      historyKey,
      close: () => onCloseRef.current(),
    };
    entryRef.current = entry;
    overlayStack.push(entry);

    return () => {
      if (!entryRef.current) return;
      const idx = overlayStack.indexOf(entryRef.current);
      if (idx >= 0) overlayStack.splice(idx, 1);
      entryRef.current = null;
    };
  }, [open, historyKey, enabled]);

  const requestClose = useCallback(() => {
    if (!enabled) {
      onCloseRef.current();
      return;
    }
    const entry = entryRef.current;
    const idx = entry ? overlayStack.lastIndexOf(entry) : -1;
    if (idx >= 0 && window.history.state?.page === historyKey) {
      window.history.back();
      return;
    }
    if (idx >= 0) overlayStack.splice(idx, 1);
    entryRef.current = null;
    onCloseRef.current();
  }, [historyKey, enabled]);

  return requestClose;
}
